/* The period being viewed: Annual (year), Quarterly (year + quarter) or Monthly (year + month). A report is viewed by its own
 * frequency (quarterly or monthly); the whole year (Annual) is viewed on the Historic screen.
 * Column indexes follow the report grid: 0-11 months, 12-15 quarters, 16 the year. */
import S from './core.js';
import { describe } from './approvals.js';

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** The latest month that has figures for a report with this as-at date (0-11). */
export const monthOfAsAt = (asAt) => Math.max(0, Math.min(11, Number(String(asAt).slice(5, 7)) - 1));

/** 'monthly' | 'quarterly'. Uses reporting_frequency (or an extra field of that name); otherwise a report that stops at a
 * quarter end is taken as quarterly and any other month end as monthly. */
export function frequencyOf(report) {
  const v = String(report.reporting_frequency || (report.extra || report.extra_fields || {})['Reporting frequency'] || '').trim().toLowerCase();
  if (v.startsWith('month')) return 'monthly';
  if (v.startsWith('quarter')) return 'quarterly';
  return Number(String(report.as_at_date).slice(5, 7)) % 3 === 0 ? 'quarterly' : 'monthly';
}

/** The period to open a report at when none is given: its own frequency, at the latest month it has figures for. */
export const defaultSelection = (report) => {
  const month = monthOfAsAt(report.as_at_date);
  return { mode: frequencyOf(report), year: report.report_year, quarter: Math.floor(month / 3), month };
};

/** -> { mode, type: 'year'|'quarter'|'month', label, start, idx, cols, title } */
export function periodOf(sel) {
  const { mode, year, quarter, month } = sel;
  if (mode === 'monthly') {
    const m = S.periodMeta(month, year);
    return { mode, type: m.type, label: m.label, start: m.start, idx: month, cols: [month], title: `${MONTH_NAMES[month]} ${year}` };
  }
  if (mode === 'quarterly') {
    const m = S.periodMeta(12 + quarter, year);
    return { mode, type: m.type, label: m.label, start: m.start, idx: 12 + quarter, cols: [3 * quarter, 3 * quarter + 1, 3 * quarter + 2, 12 + quarter], title: `${m.label} ${year}` };
  }
  const m = S.periodMeta(16, year);
  return { mode: 'annual', type: m.type, label: m.label, start: m.start, idx: 16, cols: Array.from({ length: 17 }, (_, i) => i), title: `Annual ${year}` };
}

/** Does the report hold any figure in the columns of this period? */
export function hasFigures(layout, period) {
  return layout.pages.some((pg) => pg.rows.some((r) => r.k === 'grid' && period.cols.some((i) => r.cells[i] && r.cells[i].v !== null)));
}

/** The report layout cut down to one period (Annual keeps every column), with the period and approvals added to the header block.
 * The result feeds the on-screen preview, the Excel file and the PDF alike. */
export function viewLayout(layout, period, status) {
  const full = period.cols.length === layout.heads.length;
  const pick = (a) => period.cols.map((i) => a[i]);
  const lines = [{ k: 'gap' }, { k: 'kv', label: 'Period', value: period.title }];
  if (status) {
    lines.push({ k: 'kv', label: 'Finance approved', value: describe(status.finance) || 'Pending' }, { k: 'kv', label: 'AM approved', value: describe(status.account_manager) || 'Pending' });
  }
  return {
    ...layout,
    asAt: `${layout.asAt} - ${period.title}`,
    heads: full ? layout.heads : pick(layout.heads),
    partial: !full,
    pages: full ? layout.pages : layout.pages.map((pg) => ({ ...pg, rows: pg.rows.map((r) => (r.k === 'grid' ? { ...r, cells: pick(r.cells) } : r)) })),
    headerLines: [...layout.headerLines, ...lines],
  };
}
